import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  AutoImage,
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Mention,
  Paragraph,
  SimpleUploadAdapter,
  TextTransformation,
} from 'ckeditor5';
import { casesApi } from 'src/api/casesApi';
import { projectApi } from 'src/api/projectApi';
import toast from 'react-hot-toast';
import FileUploadDragDrop from 'src/components/widgets/fileUpload/FileUploadDragDrop';
import { Skeleton } from '@mui/material';
import { userApi } from 'src/api/userApi';
import './styles.css';

interface DeclineDialogProps {
  open: boolean;
  onClose: () => void;
  onStatusChange: (status: any) => void;
  caseData: any;
  pendingStatus: any;
}

const DeclineDialog = ({
  open,
  onClose,
  onStatusChange,
  caseData,
  pendingStatus,
}: DeclineDialogProps) => {
  const [declineComment, setDeclineComment] = useState('');
  const [filePath, setFilePath] = useState<string[]>([]);
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [externalUsers, setExternalUsers] = useState({});
  const [isLoadingMentions, setIsLoadingMentions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch external users on dialog open
  const fetchExternalUsers = async () => {
    try {
      setIsLoadingMentions(true);
      const response = await projectApi.getUsersByProj({
        organizationId: caseData?.organization?.id,
      });
      const users = response?.data?.map((mentionUser) => ({
        id: `@${mentionUser.name}`,
        name: mentionUser.name,
        userId: mentionUser.id,
        isExternal: mentionUser.isExternal,
      }));

      const externalUsersMap = users.reduce((acc, u) => {
        acc[`@${u.name}`] = u.isExternal;
        return acc;
      }, {});

      setExternalUsers(externalUsersMap);
      setMentionUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoadingMentions(false);
    }
  };

  // Setup mention handling
  const onReady = useCallback(
    (editor) => {
      // Downcast for editing
      editor.conversion.for('editingDowncast').attributeToElement({
        model: 'mention',
        view: (modelAttributeValue, { writer }) => {
          if (!modelAttributeValue) return;

          const mentionId = modelAttributeValue.id;
          const isExternal = externalUsers[mentionId] || false;
          const userType = isExternal ? 'mention-external' : 'mention-internal';

          const mentionElement = writer.createAttributeElement('span', {
            class: `mention ${userType}`,
            'data-mention': mentionId,
          });

          // eslint-disable-next-line consistent-return
          return mentionElement;
        },
        converterPriority: 'high',
      });

      // Downcast for data
      editor.conversion.for('dataDowncast').attributeToElement({
        model: 'mention',
        view: (modelAttributeValue, { writer }) => {
          if (!modelAttributeValue) return;

          const mentionId = modelAttributeValue.id;
          const isExternal = externalUsers[mentionId] || false;
          const userType = isExternal ? 'mention-external' : 'mention-internal';

          const mentionElement = writer.createAttributeElement('span', {
            class: `mention ${userType}`,
            'data-mention': mentionId,
          });

          // eslint-disable-next-line consistent-return
          return mentionElement;
        },
        converterPriority: 'high',
      });

      editor.setData(editor.getData());
    },
    [externalUsers]
  );

  const handleConfirm = async () => {
    if (!declineComment.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // First create the comment
      const payload = {
        caseId: caseData?.id,
        description: declineComment,
        isExternal: true,
        attachmentPath: filePath,
        taggedUsers: mentionedUserIds,
      };

      await casesApi?.createComment(payload);

      // Only after comment is successfully created, update the status
      onStatusChange(pendingStatus);

      toast.success('Case declined successfully');
      setDeclineComment('');
      setFilePath([]);
      setMentionedUserIds([]);
      onClose();
    } catch (error) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || 'Failed to decline case');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch users when dialog opens
  useEffect(() => {
    if (open) {
      fetchExternalUsers();
    }
  }, [open]);

  function uploadAdapter(loader) {
    return {
      upload: () =>
        new Promise((resolve) => {
          loader.file.then(async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            const image = await userApi.uploadAttachments(formData);
            resolve({
              default: `${image?.data?.blob_url}`,
            });
          });
        }),
    };
  }
  function uploadPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
      uploadAdapter(loader);
  }

  const editorConfig = {
    toolbar: {
      items: [
        '|',
        'heading',
        '|',
        'bold',
        'italic',
        '|',
        'link',
        'blockQuote',
        'insertImage',
        '|',
        'bulletedList',
        'numberedList',
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AutoImage,
      BlockQuote,
      Bold,
      Essentials,
      Heading,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      List,
      ListProperties,
      Paragraph,
      SimpleUploadAdapter,
      TextTransformation,
      Mention,
    ],
    blockToolbar: [
      'bold',
      'italic',
      '|',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      'outdent',
      'indent',
    ],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
      ],
    },
    image: {
      toolbar: [
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage',
      ],
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file',
          },
        },
      },
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },
    mention: {
      feeds: [
        {
          marker: '@',
          feed: (query) => {
            // Filter mention users based on query
            if (query.length === 0) {
              return mentionUsers; // Return all users when no query
            }
            return mentionUsers.filter((mention) =>
              mention.name.toLowerCase().includes(query.toLowerCase())
            );
          },
          minimumCharacters: 0,
        },
      ],
    },
    extraPlugins: [uploadPlugin],
    placeholder: 'Enter your comment here',
  };

  return (
    <Dialog
      open={open}
      onClose={!isSubmitting ? onClose : undefined} // Prevent closing while submitting
      maxWidth="sm"
      fullWidth>
      <DialogContent>
        <DialogContentText>
          Please add an external comment explaining why this case is being
          declined:
        </DialogContentText>
        {isLoadingMentions ? (
          <Skeleton height={100} />
        ) : (
          <Box sx={{ mb: 2 }}>
            <CKEditor
              editor={ClassicEditor}
              data={declineComment}
              onReady={onReady}
              onChange={(event, editor) => {
                const data = editor.getData();
                setDeclineComment(data);

                const newMentionedUserIds = [];
                const root = editor.model.document.getRoot();

                Array.from(root.getChildren())
                  .flatMap((block: any) => Array.from(block.getChildren()))
                  .filter(
                    (item: any) =>
                      item.hasAttribute && item.hasAttribute('mention')
                  )
                  .forEach((mention: any) => {
                    const mentionUserId =
                      mention.getAttribute('mention').userId;
                    if (
                      mentionUserId &&
                      !newMentionedUserIds.includes(mentionUserId)
                    ) {
                      newMentionedUserIds.push(mentionUserId);
                    }
                  });

                setMentionedUserIds(newMentionedUserIds);
              }}
              config={editorConfig as any}
            />
            <FileUploadDragDrop setFilePath={setFilePath} filePath={filePath} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="secondary"
          disabled={!declineComment.trim() || isSubmitting}
          variant="contained"
          sx={{ position: 'relative' }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  marginLeft: '-12px',
                  color: 'secondary.main'
                }}
              />
              <span style={{ visibility: 'hidden' }}>Decline Case</span>
            </>
          ) : (
            'Decline Case'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeclineDialog;
